<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  Archive,
  Building2,
  Cable,
  CheckCircle2,
  Mail,
  RefreshCw,
  Save,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  RotateCcw,
  Trash2,
  X,
} from "lucide-vue-next";
import AppShell from "@/components/workspace/AppShell.vue";
import { useWorkspaceStore } from "@/stores/workspace";
import OrganizationsView from "@/views/OrganizationsView.vue";
import type { WorkspaceSettings } from "@/types/models";

type AdminSection =
  | "workspace"
  | "organizations"
  | "email"
  | "archives"
  | "access"
  | "integrations";

const workspace = useWorkspaceStore();
const route = useRoute();
const router = useRouter();
const loading = ref(false);
const saving = ref(false);
const loadError = ref("");
const saveError = ref("");
const saved = ref(false);
const loaded = ref(false);
const archivesLoaded = ref(false);
const archiveLoading = ref(false);
const archiveError = ref("");
const archiveActionId = ref("");
const emailTemplatesLoading = ref(false);
const emailTemplatesError = ref("");
const testEmailAddress = ref("");
const testEmailSending = ref(false);
const testEmailError = ref("");
const testEmailMessage = ref("");
const directoryLoading = ref(false);
const directoryRunning = ref(false);
const directoryError = ref("");
const directoryMessage = ref("");
const deleteProject = ref<(typeof workspace.archivedProjects)[number] | null>(
  null,
);
const deleteConfirmation = ref("");
const deleteDialog = ref<HTMLFormElement | null>(null);
const deleteConfirmationInput = ref<HTMLInputElement | null>(null);
let deleteTrigger: HTMLElement | null = null;
const draft = reactive<WorkspaceSettings>({ ...workspace.workspaceSettings });

const isAdministrator = computed(
  () => workspace.user?.role === "Workspace administrator",
);
const directoryInProgress = computed(
  () =>
    directoryRunning.value ||
    ["queued", "running", "retrying"].includes(
      workspace.directorySyncStatus?.state || "",
    ),
);
const section = computed<AdminSection>(() => {
  const requested = String(route.query.section || "workspace");
  return [
    "workspace",
    "organizations",
    "email",
    "archives",
    "access",
    "integrations",
  ].includes(requested)
    ? (requested as AdminSection)
    : "workspace";
});
const sections: Array<{
  id: AdminSection;
  label: string;
  description: string;
  icon: typeof SlidersHorizontal;
}> = [
  {
    id: "workspace",
    label: "Workspace",
    description: "Name, time zone, and invitation defaults",
    icon: SlidersHorizontal,
  },
  {
    id: "organizations",
    label: "Organizations",
    description: "Reusable client details and branding",
    icon: Building2,
  },
  {
    id: "email",
    label: "Email delivery",
    description: "Sender identity, replies, and digest timing",
    icon: Mail,
  },
  {
    id: "archives",
    label: "Archived projects",
    description: "Restore or permanently delete archived work",
    icon: Archive,
  },
  {
    id: "access",
    label: "Access",
    description: "Microsoft identity and employee eligibility",
    icon: ShieldCheck,
  },
  {
    id: "integrations",
    label: "Integrations",
    description: "Connected services and delivery status",
    icon: Cable,
  },
];

const dirty = computed(
  () => JSON.stringify(draft) !== JSON.stringify(workspace.workspaceSettings),
);

function replaceDraft(settings: WorkspaceSettings) {
  Object.assign(draft, settings);
  saved.value = false;
  saveError.value = "";
}

async function loadSettings() {
  if (!isAdministrator.value || loaded.value) return;
  loading.value = true;
  loadError.value = "";
  try {
    replaceDraft(await workspace.loadWorkspaceSettings());
    loaded.value = true;
  } catch {
    loadError.value =
      "Administration settings could not be loaded. Check your connection and try again.";
  } finally {
    loading.value = false;
  }
}

async function saveSettings() {
  if (!dirty.value || saving.value) return;
  saving.value = true;
  saved.value = false;
  saveError.value = "";
  try {
    replaceDraft(await workspace.saveWorkspaceSettings({ ...draft }));
    saved.value = true;
  } catch (error) {
    saveError.value =
      error instanceof Error
        ? `${error.message} Your changes are still on this page.`
        : "Your changes could not be saved. They are still on this page. Reload if another administrator changed these settings.";
  } finally {
    saving.value = false;
  }
}

function cancelChanges() {
  replaceDraft({ ...workspace.workspaceSettings });
}

function setSection(id: AdminSection) {
  router.replace({ query: id === "workspace" ? {} : { section: id } });
}

async function loadArchivedProjects(force = false) {
  if (!isAdministrator.value || (archivesLoaded.value && !force)) return;
  archiveLoading.value = true;
  archiveError.value = "";
  try {
    await workspace.loadArchivedProjects();
    archivesLoaded.value = true;
  } catch {
    archiveError.value =
      "Archived projects could not be loaded. Check your connection and try again.";
  } finally {
    archiveLoading.value = false;
  }
}

async function loadEmailTemplates() {
  if (
    !isAdministrator.value ||
    workspace.emailTemplates.length ||
    emailTemplatesLoading.value
  )
    return;
  emailTemplatesLoading.value = true;
  emailTemplatesError.value = "";
  try {
    await workspace.loadEmailTemplates();
  } catch {
    emailTemplatesError.value =
      "Email templates could not be loaded. Check your connection and try again.";
  } finally {
    emailTemplatesLoading.value = false;
  }
}

async function sendTestMessage() {
  if (testEmailSending.value || !testEmailAddress.value.trim()) return;
  testEmailSending.value = true;
  testEmailError.value = "";
  testEmailMessage.value = "";
  try {
    let delivery = await workspace.sendTestEmail(testEmailAddress.value.trim());
    if (delivery.state === "emulator_queued") {
      testEmailMessage.value =
        "Test email created in the local delivery preview.";
      return;
    }
    for (let attempt = 0; attempt < 8; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 750));
      delivery = await workspace.testEmailStatus(delivery.id);
      if (["accepted", "delivered", "failed"].includes(delivery.state)) break;
    }
    testEmailMessage.value = ["accepted", "delivered"].includes(delivery.state)
      ? `Test email ${delivery.state === "delivered" ? "was delivered" : "was accepted by the delivery service"}.`
      : delivery.state === "failed"
        ? "The delivery service could not send the test email. Check the email service configuration and try again."
        : "Test email is queued. Delivery status will continue updating in the background.";
  } catch (error) {
    testEmailError.value =
      error instanceof Error
        ? error.message
        : "The test email could not be sent. Check the delivery settings and try again.";
  } finally {
    testEmailSending.value = false;
  }
}

async function loadDirectoryStatus() {
  if (!isAdministrator.value || directoryLoading.value) return;
  directoryLoading.value = true;
  directoryError.value = "";
  try {
    await workspace.loadDirectorySyncStatus();
  } catch (error) {
    directoryError.value =
      error instanceof Error
        ? error.message
        : "Directory sync status could not be loaded. Check your connection and try again.";
  } finally {
    directoryLoading.value = false;
  }
}

async function runDirectorySync() {
  if (directoryInProgress.value) return;
  directoryRunning.value = true;
  directoryError.value = "";
  directoryMessage.value = "";
  let queued = false;
  try {
    await workspace.runDirectorySync();
    queued = true;
    directoryMessage.value =
      "Corporate Team sync queued. You may leave this page while it runs.";
    for (let attempt = 0; attempt < 12; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 1_000));
      await workspace.loadDirectorySyncStatus();
      if (
        ["completed", "failed"].includes(
          workspace.directorySyncStatus?.state || "",
        )
      )
        break;
    }
    if (workspace.directorySyncStatus?.state === "completed")
      directoryMessage.value = "Corporate Team directory sync completed.";
    if (workspace.directorySyncStatus?.state === "failed")
      directoryError.value =
        "The directory sync could not be completed. Existing directory profiles are unchanged. Check the Microsoft connection and try again.";
  } catch (error) {
    directoryError.value = queued
      ? "The sync was queued, but its latest status could not be loaded. You may leave this page and check again later."
      : error instanceof Error
        ? error.message
        : "The directory sync could not be queued. Try again.";
  } finally {
    directoryRunning.value = false;
  }
}

function directoryTimestamp(value: string) {
  if (!value) return "Not run yet";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "Not run yet"
    : new Intl.DateTimeFormat("en-CA", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(parsed);
}

async function restoreProject(projectId: string) {
  archiveActionId.value = projectId;
  archiveError.value = "";
  try {
    await workspace.restoreArchivedProject(projectId);
  } catch (error) {
    archiveError.value =
      error instanceof Error
        ? error.message
        : "The project could not be restored. Try again.";
  } finally {
    archiveActionId.value = "";
  }
}

async function confirmDeleteProject() {
  if (!deleteProject.value || deleteConfirmation.value !== "delete") return;
  archiveActionId.value = deleteProject.value.id;
  archiveError.value = "";
  try {
    await workspace.deleteArchivedProject(
      deleteProject.value.id,
      deleteConfirmation.value,
    );
    deleteProject.value = null;
    deleteConfirmation.value = "";
  } catch (error) {
    archiveError.value =
      error instanceof Error
        ? error.message
        : "The project could not be fully deleted. Reload the archive before trying again.";
  } finally {
    archiveActionId.value = "";
  }
}

function closeDeleteDialog() {
  if (archiveActionId.value) return;
  deleteProject.value = null;
  deleteConfirmation.value = "";
}

function handleDeleteDialogKeys(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    closeDeleteDialog();
    return;
  }
  if (event.key !== "Tab" || !deleteDialog.value) return;
  const controls = [
    ...deleteDialog.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  ];
  if (!controls.length) return;
  const first = controls[0]!;
  const last = controls.at(-1)!;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

watch(
  () => workspace.user,
  () => {
    void loadSettings();
    void loadEmailTemplates();
    if (section.value === "archives") void loadArchivedProjects();
    if (section.value === "integrations") void loadDirectoryStatus();
  },
  { immediate: true },
);
watch(section, (value) => {
  if (value === "archives") void loadArchivedProjects();
  if (value === "integrations") void loadDirectoryStatus();
});
watch(deleteProject, async (project) => {
  if (project) {
    deleteTrigger =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    await nextTick();
    deleteConfirmationInput.value?.focus();
  } else {
    deleteTrigger?.focus();
    deleteTrigger = null;
  }
});
onMounted(() => {
  void loadSettings();
  void loadEmailTemplates();
  if (section.value === "integrations") void loadDirectoryStatus();
});
</script>

<template>
  <AppShell>
    <div class="admin-page">
      <header class="admin-header">
        <div>
          <h1>Administration</h1>
          <p>Manage workspace defaults and communication settings.</p>
        </div>
        <div v-if="isAdministrator && loaded" class="admin-header__actions">
          <span v-if="saved" class="save-confirmation" role="status">
            <CheckCircle2 :size="16" /> Saved
          </span>
          <button
            v-if="dirty"
            class="button button--secondary"
            type="button"
            :disabled="saving"
            @click="cancelChanges"
          >
            Cancel
          </button>
          <button
            class="button button--primary"
            type="button"
            :disabled="!dirty || saving"
            @click="saveSettings"
          >
            <RefreshCw v-if="saving" class="spin" :size="16" />
            <Save v-else :size="16" />
            {{ saving ? "Saving…" : "Save configuration" }}
          </button>
        </div>
      </header>

      <section
        v-if="!workspace.user || loading"
        class="admin-state sheet"
        aria-live="polite"
      >
        <RefreshCw class="spin" :size="22" />
        <div>
          <strong>Loading administration</strong
          ><span>Retrieving workspace settings…</span>
        </div>
      </section>

      <section v-else-if="!isAdministrator" class="admin-state sheet">
        <ShieldCheck :size="25" />
        <div>
          <h2>Administrator access required</h2>
          <p>
            Only a VIPdesk workspace administrator can view or change these
            settings.
          </p>
        </div>
        <button
          class="button button--secondary"
          type="button"
          @click="router.push('/projects')"
        >
          Return to projects
        </button>
      </section>

      <section
        v-else-if="loadError"
        class="admin-state admin-state--error sheet"
        role="alert"
      >
        <div>
          <h2>Settings are unavailable</h2>
          <p>{{ loadError }}</p>
        </div>
        <button
          class="button button--secondary"
          type="button"
          @click="loadSettings"
        >
          Try again
        </button>
      </section>

      <div v-else class="admin-workspace">
        <nav class="admin-sections" aria-label="Administration sections">
          <button
            v-for="item in sections"
            :key="item.id"
            type="button"
            :class="{ 'admin-section--active': section === item.id }"
            :aria-current="section === item.id ? 'page' : undefined"
            @click="setSection(item.id)"
          >
            <component :is="item.icon" :size="18" aria-hidden="true" />
            <span
              ><strong>{{ item.label }}</strong
              ><small>{{ item.description }}</small></span
            >
          </button>
        </nav>

        <main
          class="admin-detail"
          :class="{
            sheet: section !== 'organizations',
            'admin-detail--organizations': section === 'organizations',
          }"
        >
          <OrganizationsView v-if="section === 'organizations'" embedded />
          <template v-else>
            <div v-if="saveError" class="form-alert" role="alert">
              {{ saveError }}
            </div>

            <form v-if="section === 'workspace'" @submit.prevent="saveSettings">
              <header>
                <h2>Workspace defaults</h2>
                <p>
                  These values provide consistent starting points for new
                  projects and invitations.
                </p>
              </header>
              <div class="admin-form-grid">
                <label class="field field--wide">
                  <span>Workspace name</span>
                  <input
                    v-model.trim="draft.workspaceName"
                    required
                    minlength="2"
                    maxlength="120"
                  />
                  <small
                    >Shown in workspace navigation and administrative
                    communication.</small
                  >
                </label>
                <label class="field">
                  <span>Default time zone</span>
                  <select v-model="draft.defaultTimezone">
                    <option value="America/Toronto">Eastern — Toronto</option>
                    <option value="America/Chicago">Central — Chicago</option>
                    <option value="America/Denver">Mountain — Denver</option>
                    <option value="America/Los_Angeles">
                      Pacific — Los Angeles
                    </option>
                    <option value="UTC">UTC</option>
                  </select>
                </label>
                <label class="field">
                  <span>Invitation expires after</span>
                  <span class="field-with-suffix"
                    ><input
                      v-model.number="draft.invitationExpiryDays"
                      type="number"
                      min="1"
                      max="90"
                      required
                    /><span>days</span></span
                  >
                </label>
                <label class="field field--wide">
                  <span>Support email</span>
                  <input
                    v-model.trim="draft.supportEmail"
                    type="email"
                    required
                    maxlength="320"
                  />
                  <small
                    >Displayed when a participant needs access or workspace
                    help.</small
                  >
                </label>
              </div>
              <section
                class="email-templates"
                aria-labelledby="email-templates-title"
              >
                <header>
                  <h3 id="email-templates-title">Email templates</h3>
                  <p>
                    These are the messages currently used for invitations,
                    project access, task comments, and participant digests.
                    Preview content uses example names and dates.
                  </p>
                </header>
                <p v-if="emailTemplatesError" class="form-alert" role="alert">
                  {{ emailTemplatesError }}
                </p>
                <div
                  v-if="emailTemplatesLoading"
                  class="template-loading"
                  role="status"
                >
                  <RefreshCw class="spin" :size="17" /> Loading templates…
                </div>
                <details
                  v-for="template in workspace.emailTemplates"
                  :key="template.id"
                  class="email-template"
                >
                  <summary>
                    <span>
                      <strong>{{ template.name }}</strong>
                      <small>{{ template.description }}</small>
                    </span>
                    <span>
                      <small>{{ template.audience }}</small>
                      <strong>{{ template.subject }}</strong>
                    </span>
                  </summary>
                  <div class="email-template__details">
                    <dl>
                      <div>
                        <dt>Sent when</dt>
                        <dd>{{ template.trigger }}</dd>
                      </div>
                      <div>
                        <dt>Subject</dt>
                        <dd>{{ template.subject }}</dd>
                      </div>
                    </dl>
                    <iframe
                      sandbox=""
                      :title="`${template.name} email preview`"
                      :srcdoc="template.html"
                    ></iframe>
                  </div>
                </details>
              </section>
            </form>

            <form
              v-else-if="section === 'email'"
              @submit.prevent="saveSettings"
            >
              <header>
                <h2>Email delivery</h2>
                <p>
                  Control the identity and timing used for project notifications
                  and digests.
                </p>
              </header>
              <div class="delivery-status">
                <span
                  :class="{
                    'delivery-status__dot--active':
                      draft.emailDeliveryMode !== 'disabled',
                  }"
                ></span>
                <div>
                  <strong>{{
                    draft.emailDeliveryMode === "ses"
                      ? workspace.usingDemo
                        ? "Amazon SES is available after deployment"
                        : "Amazon SES delivery is active"
                      : draft.emailDeliveryMode === "emulator"
                        ? "Local delivery preview is active"
                        : "Email delivery is disabled"
                  }}</strong>
                  <p>
                    {{
                      workspace.usingDemo && draft.emailDeliveryMode === "ses"
                        ? "Development never sends external email. Choose Local emulator preview here, then test SES from the deployed application."
                        : "No production provider credentials are stored or displayed in this application."
                    }}
                  </p>
                </div>
              </div>
              <section class="email-test" aria-labelledby="email-test-title">
                <div>
                  <h3 id="email-test-title">Send a test email</h3>
                  <p>
                    Send a message with no project data to confirm the
                    configured delivery path.
                  </p>
                </div>
                <div class="email-test__form">
                  <label class="field">
                    <span>Email address</span>
                    <input
                      v-model.trim="testEmailAddress"
                      type="email"
                      autocomplete="email"
                      maxlength="320"
                      required
                      placeholder="name@example.com"
                    />
                  </label>
                  <button
                    class="button button--secondary"
                    type="button"
                    :disabled="testEmailSending || !testEmailAddress.trim()"
                    @click="sendTestMessage"
                  >
                    <Send :size="16" />
                    {{ testEmailSending ? "Sending test…" : "Send test email" }}
                  </button>
                </div>
                <p v-if="testEmailError" class="form-alert" role="alert">
                  {{ testEmailError }}
                </p>
                <p v-if="testEmailMessage" class="form-success" role="status">
                  {{ testEmailMessage }}
                </p>
              </section>
              <div class="admin-form-grid">
                <label class="field field--wide">
                  <span>Delivery mode</span>
                  <select v-model="draft.emailDeliveryMode">
                    <option value="disabled">Disabled</option>
                    <option value="emulator">Local emulator preview</option>
                    <option value="ses" :disabled="workspace.usingDemo">
                      Amazon SES{{
                        workspace.usingDemo
                          ? " — deployed application only"
                          : ""
                      }}
                    </option>
                  </select>
                </label>
                <label class="field">
                  <span>Sender name</span>
                  <input
                    v-model.trim="draft.emailSenderName"
                    required
                    minlength="2"
                    maxlength="120"
                  />
                </label>
                <label class="field">
                  <span>Reply-to email</span>
                  <input
                    v-model.trim="draft.emailReplyTo"
                    type="email"
                    required
                    maxlength="320"
                  />
                </label>
                <label class="field field--wide">
                  <span>Daily digest delivery</span>
                  <select v-model.number="draft.digestHour">
                    <option
                      v-for="hour in 24"
                      :key="hour - 1"
                      :value="hour - 1"
                    >
                      {{ String(hour - 1).padStart(2, "0") }}:00 in the
                      recipient’s time zone
                    </option>
                  </select>
                </label>
              </div>
            </form>

            <section v-else-if="section === 'access'" class="admin-information">
              <header>
                <h2>Identity and access</h2>
                <p>
                  Review how people enter the workspace and what proves employee
                  eligibility.
                </p>
              </header>
              <dl>
                <div>
                  <dt>Employee sign-in</dt>
                  <dd>Microsoft 365</dd>
                  <span>Production tenant connection required</span>
                </div>
                <div>
                  <dt>Guest access</dt>
                  <dd>Project invitation only</dd>
                  <span
                    >Organization membership does not grant project access</span
                  >
                </div>
                <div>
                  <dt>Project creation</dt>
                  <dd>Verified eligible VIPdesk employees</dd>
                  <span>Email domain alone is never accepted as proof</span>
                </div>
              </dl>
              <p class="admin-note">
                Tenant identifiers, application secrets, and eligibility-group
                connections are managed through deployment configuration and
                Secret Manager. They are intentionally unavailable in the
                browser.
              </p>
            </section>

            <section
              v-else-if="section === 'archives'"
              class="admin-information archives-section"
            >
              <header>
                <h2>Archived projects</h2>
                <p>
                  Restore completed work to the project list, or permanently
                  delete its project data and participant access.
                </p>
              </header>
              <p v-if="archiveError" class="form-alert" role="alert">
                {{ archiveError }}
              </p>
              <div v-if="archiveLoading" class="archive-state" role="status">
                <RefreshCw class="spin" :size="18" /> Loading archived projects…
              </div>
              <div
                v-else-if="!workspace.archivedProjects.length"
                class="archive-state"
              >
                <Archive :size="23" />
                <strong>No archived projects</strong>
                <span
                  >Projects appear here after a project owner archives
                  them.</span
                >
              </div>
              <div v-else class="archive-list">
                <article
                  v-for="project in workspace.archivedProjects"
                  :key="project.id"
                  class="archive-row"
                >
                  <span class="archive-logo">
                    <img
                      v-if="project.clientOrganizationLogoDataUrl"
                      :src="project.clientOrganizationLogoDataUrl"
                      :alt="`${project.client} logo`"
                    />
                    <template v-else>{{
                      project.client.slice(0, 2).toUpperCase()
                    }}</template>
                  </span>
                  <span>
                    <strong>{{ project.name }}</strong>
                    <small>{{ project.code }} · {{ project.client }}</small>
                  </span>
                  <time>{{ project.updatedAt }}</time>
                  <span class="archive-actions">
                    <button
                      class="button button--secondary"
                      type="button"
                      :disabled="archiveActionId === project.id"
                      @click="restoreProject(project.id)"
                    >
                      <RotateCcw :size="15" /> Restore project
                    </button>
                    <button
                      class="button button--danger"
                      type="button"
                      :disabled="archiveActionId === project.id"
                      @click="
                        deleteProject = project;
                        deleteConfirmation = '';
                      "
                    >
                      <Trash2 :size="15" /> Delete
                    </button>
                  </span>
                </article>
              </div>
            </section>

            <section v-else class="admin-information">
              <header>
                <h2>Integrations</h2>
                <p>
                  Review connected services and the background work that keeps
                  them current.
                </p>
              </header>
              <section
                class="directory-sync"
                aria-labelledby="directory-sync-title"
              >
                <header>
                  <div>
                    <h3 id="directory-sync-title">Microsoft directory</h3>
                    <p>
                      Corporate Team members are available in project people
                      pickers. Directory membership never grants project access
                      on its own.
                    </p>
                  </div>
                  <button
                    class="button button--secondary"
                    type="button"
                    :disabled="
                      directoryInProgress ||
                      directoryLoading ||
                      !workspace.directorySyncStatus?.configured
                    "
                    @click="runDirectorySync"
                  >
                    <RefreshCw
                      :class="{ spin: directoryInProgress }"
                      :size="16"
                    />
                    {{
                      directoryInProgress
                        ? "Syncing Corporate Team…"
                        : "Run directory sync"
                    }}
                  </button>
                </header>
                <div
                  v-if="directoryLoading && !workspace.directorySyncStatus"
                  class="directory-sync__loading"
                  role="status"
                >
                  <RefreshCw class="spin" :size="17" /> Loading directory
                  status…
                </div>
                <template v-else-if="workspace.directorySyncStatus">
                  <div class="directory-sync__status">
                    <span
                      class="delivery-status__dot"
                      :class="{
                        'delivery-status__dot--active':
                          workspace.directorySyncStatus.configured &&
                          workspace.directorySyncStatus.state !== 'failed',
                      }"
                      aria-hidden="true"
                    ></span>
                    <div>
                      <strong>{{
                        workspace.directorySyncStatus.configured
                          ? workspace.directorySyncStatus.groupName
                          : "Microsoft directory configuration required"
                      }}</strong>
                      <span>{{ workspace.directorySyncStatus.schedule }}</span>
                    </div>
                  </div>
                  <dl class="directory-sync__facts">
                    <div>
                      <dt>Available people</dt>
                      <dd>
                        {{ workspace.directorySyncStatus.activeProfileCount }}
                      </dd>
                      <dd class="fact-note">Active Corporate Team profiles</dd>
                    </div>
                    <div>
                      <dt>Last successful sync</dt>
                      <dd>
                        {{
                          directoryTimestamp(
                            workspace.directorySyncStatus.lastSuccessfulRunAt,
                          )
                        }}
                      </dd>
                      <dd class="fact-note">
                        {{ workspace.directorySyncStatus.inactiveProfileCount }}
                        inactive in the last completed run
                      </dd>
                    </div>
                    <div>
                      <dt>Current state</dt>
                      <dd>
                        {{
                          workspace.directorySyncStatus.state === "not_run"
                            ? "Not run"
                            : workspace.directorySyncStatus.state.replaceAll(
                                "_",
                                " ",
                              )
                        }}
                      </dd>
                      <dd class="fact-note">
                        Names, titles, avatars, and eligibility are refreshed
                        together
                      </dd>
                    </div>
                  </dl>
                </template>
                <p v-if="directoryError" class="form-alert" role="alert">
                  {{ directoryError }}
                </p>
                <p v-if="directoryMessage" class="form-success" role="status">
                  {{ directoryMessage }}
                </p>
              </section>
              <dl class="planned-integrations">
                <div>
                  <dt>Microsoft Teams</dt>
                  <dd>Not connected</dd>
                  <dd class="fact-note">Planned notification channel</dd>
                </div>
                <div>
                  <dt>Slack</dt>
                  <dd>Not connected</dd>
                  <dd class="fact-note">Planned notification channel</dd>
                </div>
              </dl>
            </section>
          </template>
        </main>
      </div>
      <Teleport to="body">
        <div v-if="deleteProject" class="delete-layer">
          <button
            class="delete-scrim"
            type="button"
            aria-label="Cancel project deletion"
            @click="closeDeleteDialog"
          ></button>
          <form
            ref="deleteDialog"
            class="delete-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-project-title"
            aria-describedby="delete-project-description"
            @keydown="handleDeleteDialogKeys"
            @submit.prevent="confirmDeleteProject"
          >
            <header>
              <div>
                <h2 id="delete-project-title">Delete archived project?</h2>
                <p id="delete-project-description">
                  This permanently removes {{ deleteProject.name }}, its plan,
                  files, comments, notifications, and participant access. This
                  cannot be undone.
                </p>
              </div>
              <button
                class="icon-button"
                type="button"
                aria-label="Cancel project deletion"
                @click="closeDeleteDialog"
              >
                <X :size="19" />
              </button>
            </header>
            <label class="field">
              <span>Type <strong>delete</strong> to confirm</span>
              <input
                ref="deleteConfirmationInput"
                v-model="deleteConfirmation"
                autocomplete="off"
                spellcheck="false"
              />
            </label>
            <footer>
              <button
                class="button button--secondary"
                type="button"
                @click="closeDeleteDialog"
              >
                Keep project
              </button>
              <button
                class="button button--danger"
                type="submit"
                :disabled="
                  deleteConfirmation !== 'delete' ||
                  archiveActionId === deleteProject.id
                "
              >
                <Trash2 :size="15" />
                {{
                  archiveActionId === deleteProject.id
                    ? "Deleting…"
                    : "Delete project"
                }}
              </button>
            </footer>
          </form>
        </div>
      </Teleport>
    </div>
  </AppShell>
</template>

<style scoped>
.admin-page {
  width: min(1380px, 100%);
  margin: 0 auto;
  padding: 30px 34px 60px;
}
.admin-header {
  min-height: 68px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}
.admin-header p {
  margin-top: 6px;
  color: var(--text-soft);
}
.admin-header__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.save-confirmation {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--success);
  font-weight: 700;
  font-size: 13px;
}
.admin-workspace {
  margin-top: 24px;
  display: grid;
  grid-template-columns: 270px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}
.admin-sections {
  display: grid;
  gap: 4px;
}
.admin-sections button {
  width: 100%;
  display: grid;
  grid-template-columns: 20px 1fr;
  align-items: start;
  gap: 11px;
  padding: 12px;
  border-radius: 10px;
  background: transparent;
  color: var(--text-soft);
  text-align: left;
  cursor: pointer;
}
.admin-sections button:hover {
  background: var(--sheet);
  color: var(--text);
}
.admin-sections button:focus-visible {
  outline: 3px solid var(--focus);
  outline-offset: 2px;
}
.admin-sections .admin-section--active {
  background: var(--ink-900);
  color: white;
}
.admin-sections strong,
.admin-sections small {
  display: block;
}
.admin-sections strong {
  color: inherit;
  font-size: 14px;
}
.admin-sections small {
  margin-top: 3px;
  color: inherit;
  font-size: 11px;
  line-height: 1.4;
}
.admin-detail {
  min-height: 530px;
  padding: 30px;
}
.admin-detail--organizations {
  min-width: 0;
}
.admin-detail header {
  max-width: 68ch;
  padding-bottom: 22px;
  border-bottom: 1px solid var(--rule);
}
.admin-detail header p {
  margin-top: 7px;
  color: var(--text-soft);
  line-height: 1.55;
}
.admin-form-grid {
  max-width: 800px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
  padding-top: 26px;
}
.field {
  display: grid;
  gap: 7px;
  color: var(--text);
  font-size: 13px;
  font-weight: 700;
}
.field--wide {
  grid-column: 1 / -1;
}
.field input,
.field select {
  width: 100%;
  min-height: 42px;
  padding: 9px 11px;
  border: 1px solid var(--rule-strong);
  border-radius: 8px;
  background: white;
  color: var(--text);
  font: inherit;
  font-weight: 500;
}
.field input:focus,
.field select:focus {
  outline: 3px solid var(--focus);
  border-color: var(--brand-strong);
}
.field > small {
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.45;
}
.field-with-suffix {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  border: 1px solid var(--rule-strong);
  border-radius: 8px;
  overflow: hidden;
}
.field-with-suffix input {
  border: 0;
  border-radius: 0;
}
.field-with-suffix > span {
  padding: 0 12px;
  color: var(--text-soft);
  font-size: 12px;
}
.delivery-status {
  max-width: 800px;
  margin-top: 24px;
  padding: 15px 0;
  display: grid;
  grid-template-columns: 12px 1fr;
  gap: 12px;
  align-items: start;
  border-bottom: 1px solid var(--rule);
}
.delivery-status > span {
  width: 10px;
  height: 10px;
  margin-top: 5px;
  border-radius: 50%;
  background: var(--text-faint);
}
.delivery-status .delivery-status__dot--active {
  background: var(--success);
}
.delivery-status p {
  margin-top: 3px;
  color: var(--text-soft);
  font-size: 12px;
}
.email-test {
  max-width: 800px;
  padding: 22px 0;
  border-bottom: 1px solid var(--rule);
}
.email-test > div:first-child p {
  margin-top: 4px;
  color: var(--text-soft);
}
.email-test__form {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) auto;
  align-items: end;
  gap: 10px;
  margin-top: 16px;
}
.email-test .form-alert,
.email-test .form-success {
  margin: 12px 0 0;
}
.form-success {
  padding: 12px 14px;
  border: 1px solid var(--success);
  border-radius: 8px;
  background: var(--success-soft);
  color: var(--success);
  font-size: 13px;
}
.email-templates {
  margin-top: 30px;
  padding-top: 26px;
  border-top: 1px solid var(--rule-strong);
}
.email-templates > header {
  padding: 0 0 16px;
  border: 0;
}
.email-templates > header p {
  margin-top: 4px;
  color: var(--text-soft);
}
.template-loading {
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--text-soft);
}
.email-template {
  border-top: 1px solid var(--rule);
}
.email-template:last-child {
  border-bottom: 1px solid var(--rule);
}
.email-template summary {
  min-height: 72px;
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(220px, 1fr);
  align-items: center;
  gap: 18px;
  padding: 10px 4px;
  cursor: pointer;
}
.email-template summary > span {
  min-width: 0;
  display: grid;
  gap: 3px;
}
.email-template summary small {
  color: var(--text-soft);
}
.email-template__details {
  display: grid;
  grid-template-columns: minmax(180px, 0.7fr) minmax(320px, 1.3fr);
  gap: 18px;
  padding: 0 0 20px;
}
.email-template__details dl {
  margin: 0;
}
.email-template__details dl > div {
  display: grid;
  gap: 3px;
  padding: 10px 0;
  border-bottom: 1px solid var(--rule);
}
.email-template__details dt {
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}
.email-template__details dd {
  margin: 0;
}
.email-template iframe {
  width: 100%;
  height: 440px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  background: var(--paper);
}
.admin-information dl {
  max-width: 800px;
  margin: 24px 0 0;
}
.admin-information dl > div {
  display: grid;
  grid-template-columns: minmax(150px, 0.8fr) 1fr 1.5fr;
  gap: 20px;
  padding: 16px 0;
  border-bottom: 1px solid var(--rule);
}
.admin-information dt {
  color: var(--text-soft);
  font-size: 12px;
  font-weight: 700;
}
.admin-information dd {
  margin: 0;
  font-weight: 750;
}
.admin-information dd.fact-note {
  color: var(--text-soft);
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
}
.directory-sync {
  max-width: 880px;
  margin-top: 24px;
  padding: 20px;
  border: 1px solid var(--rule-strong);
  border-radius: var(--radius-md);
  background: var(--sheet-subtle);
}
.directory-sync > header {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 24px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--rule);
}
.directory-sync > header h3,
.directory-sync > header p {
  margin: 0;
}
.directory-sync > header p {
  max-width: 64ch;
  margin-top: 4px;
  color: var(--text-soft);
}
.directory-sync__loading {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--text-soft);
}
.directory-sync__status {
  min-height: 66px;
  display: grid;
  grid-template-columns: 12px 1fr;
  align-items: center;
  gap: 12px;
}
.directory-sync__status > div {
  display: grid;
  gap: 3px;
}
.directory-sync__status span:not(.delivery-status__dot) {
  color: var(--text-soft);
  font-size: 12px;
}
.delivery-status__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--text-faint);
}
.delivery-status__dot--active {
  background: var(--success);
}
.directory-sync .directory-sync__facts {
  max-width: none;
  margin: 0;
  border-top: 1px solid var(--rule);
}
.directory-sync .directory-sync__facts > div {
  grid-template-columns: minmax(140px, 0.7fr) minmax(180px, 1fr) 1.35fr;
  gap: 16px;
  padding: 14px 0;
}
.directory-sync .form-alert,
.directory-sync .form-success {
  margin: 14px 0 0;
}
.planned-integrations {
  margin-top: 28px !important;
}
.admin-note {
  max-width: 75ch;
  margin-top: 24px;
  padding: 14px 16px;
  border: 1px solid var(--rule);
  border-radius: 10px;
  background: var(--sheet-subtle);
  color: var(--text-soft);
  font-size: 12px;
  line-height: 1.55;
}
.admin-state {
  margin-top: 24px;
  min-height: 130px;
  padding: 28px;
  display: flex;
  align-items: center;
  gap: 18px;
}
.admin-state div {
  display: grid;
  gap: 4px;
}
.admin-state div span,
.admin-state p {
  color: var(--text-soft);
}
.admin-state button {
  margin-left: auto;
}
.admin-state--error {
  color: var(--danger);
}
.form-alert {
  margin-bottom: 20px;
  padding: 12px 14px;
  border: 1px solid var(--danger);
  border-radius: 8px;
  background: var(--danger-soft);
  color: var(--danger);
  font-size: 13px;
}
.archives-section {
  min-width: 0;
}
.archive-state {
  min-height: 180px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 7px;
  color: var(--text-soft);
  text-align: center;
}
.archive-list {
  margin-top: 18px;
  border-block: 1px solid var(--rule);
}
.archive-row {
  min-height: 72px;
  display: grid;
  grid-template-columns: 42px minmax(180px, 1fr) 110px auto;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--rule);
}
.archive-row:last-child {
  border-bottom: 0;
}
.archive-row > span:nth-child(2) {
  min-width: 0;
  display: grid;
  gap: 3px;
}
.archive-row small,
.archive-row time {
  color: var(--text-soft);
  font-size: 12px;
}
.archive-logo {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  overflow: hidden;
  border: 1px solid var(--rule);
  border-radius: 5px;
  background: var(--sheet-subtle);
  color: var(--ink-800);
  font-size: 11px;
  font-weight: 800;
}
.archive-logo img {
  width: 100%;
  height: 100%;
  padding: 3px;
  object-fit: contain;
}
.archive-actions {
  display: flex;
  gap: 7px;
}
.delete-layer {
  position: fixed;
  z-index: 120;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
}
.delete-scrim {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(13, 35, 50, 0.5);
}
.delete-dialog {
  position: relative;
  width: min(540px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  display: grid;
  gap: 20px;
  padding: 24px;
  border-radius: 10px;
  background: var(--sheet);
  box-shadow: var(--shadow-panel);
}
.delete-dialog > header,
.delete-dialog > footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.delete-dialog > header p {
  max-width: 62ch;
  margin-top: 5px;
  color: var(--text-soft);
}
.delete-dialog > footer {
  justify-content: flex-end;
}
.spin {
  animation: spin 0.9s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .spin {
    animation: none;
  }
}
@media (max-width: 860px) {
  .admin-page {
    padding: 22px 18px 46px;
  }
  .admin-header {
    display: grid;
  }
  .admin-header__actions {
    position: sticky;
    top: calc(var(--top-height) + 8px);
    z-index: 5;
    justify-content: flex-end;
    padding: 8px;
    background: var(--paper);
  }
  .admin-workspace {
    grid-template-columns: 1fr;
    gap: 14px;
  }
  .admin-sections {
    grid-template-columns: repeat(5, minmax(140px, 1fr));
    overflow-x: auto;
    padding-bottom: 4px;
  }
  .admin-sections button {
    height: 100%;
  }
  .admin-detail {
    min-height: 0;
    padding: 22px 18px;
  }
}
@media (max-width: 620px) {
  .directory-sync {
    padding: 16px;
  }
  .directory-sync > header {
    align-items: stretch;
    flex-direction: column;
    gap: 14px;
  }
  .directory-sync > header .button {
    width: 100%;
    min-height: 44px;
  }
  .directory-sync .directory-sync__facts > div {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  .admin-sections {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    overflow-x: visible;
  }
  .admin-header__actions .save-confirmation {
    display: none;
  }
  .admin-form-grid {
    grid-template-columns: 1fr;
  }
  .email-test__form {
    grid-template-columns: 1fr;
  }
  .email-test__form .button {
    justify-self: start;
  }
  .field--wide {
    grid-column: auto;
  }
  .admin-information dl > div {
    grid-template-columns: 1fr;
    gap: 5px;
  }
  .email-template summary,
  .email-template__details {
    grid-template-columns: 1fr;
  }
  .archive-row {
    grid-template-columns: 42px 1fr;
  }
  .archive-row time,
  .archive-actions {
    grid-column: 2;
  }
  .archive-actions {
    flex-wrap: wrap;
  }
}
</style>
